import * as THREE from "three" 
import { useTextAnnotationDialogStore } from "../store/textAnnotationDialogStore";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListener from "../logic/CanvasInputListener";
import { sceneUnitsFromMm } from "../utils/scaler";
import GeometryCommand from "./GeometryCommand";
import TextAnnotationData from "../model/sceneObjectData/TextAnnotationData";
import RaycastIntersection from "../logic/RaycastIntersection";
import { getUpDirectionForAnnotationByPlaneNormal } from "../utils/annotationUtils";
import ISceneObject from "../model/sceneObject/ISceneObject";
import createSceneObject from "../model/sceneObject/SceneObjectFactory";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import { useUserStateStore } from "../store/userStateStore";

enum AddTextAnnotationCmdState {
    SETTING_FIRST_PT, // No points set
    ENTERING_TEXT, 
    SETTING_OFFSET, // Second point set
    FINISHED
}

const offsetRecoil = sceneUnitsFromMm(10.0); // Distance from text to target plane

export default class AddTextAnnotationCmd extends GeometryCommand  {
    cmdState: AddTextAnnotationCmdState = AddTextAnnotationCmdState.SETTING_FIRST_PT; // State of the command
    data: TextAnnotationData | undefined;
    sceneObject: ISceneObject | null = null;

    constructor(commandStack: CommandStack, canvasInputListener: CanvasInputListener, document: Document) {
        super(CommandIdEnum.AddTextAnnotation, commandStack, canvasInputListener, document ); // Call parent constructor
        this.commandNameResourceKey = 'command.addTextAnnotation.title';
        this.initialPrompResourceKey = 'command.addTextAnnotation.setReferencePoint';
    }

    start() {
        super.start();
        super.addToStack();
        this.activate();
    }

    activate() {
        this.connectMouse();
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        this.canvasInputListener.resetTargetObjects();
    }

    deactivate() {
        this.disconnectMouse();
    }

    getObject() : ISceneObject
    {
        if (!this.sceneObject) {
            if (!this.data) {
                throw new Error("Data for TextAnnotationCmd is not set");
            }
            this.sceneObject = createSceneObject(this.data); // Create a new geometry if it doesn't exist
            this.addToDrawables(this.sceneObject); // Add the scene object to the drawables
        }
        return this.sceneObject;
    }

    pointerMove(raycastResult: RaycastIntersection): boolean {

        if (this.cmdState === AddTextAnnotationCmdState.SETTING_FIRST_PT) {
            if (raycastResult && (raycastResult.normalInWcs || this.planeGridHelper.activeNormal)) {
                this.showPlaneGrid(raycastResult.point, raycastResult.normalInWcs);
            } else {
                this.hidePlaneGrid();
            }
        } else if (this.cmdState === AddTextAnnotationCmdState.SETTING_OFFSET && this.data) {
            this.showPlaneGrid(this.data.position, null);
        }
        
        if (!raycastResult) {
            return false;
        }

        if (!this.data) {
            return false;
        }

        //const point = raycastResult.point.clone(); // Get the point from the raycast result

        const point = this.getRefLineAlignedPoint(raycastResult.point.clone()); 
        this.updateRefLine(point);

        super.addRecoil(point, offsetRecoil); // Add recoil to the point

        switch (this.cmdState) {
            case AddTextAnnotationCmdState.SETTING_FIRST_PT:
                break;
            case AddTextAnnotationCmdState.SETTING_OFFSET:
                this.data!.offset.copy(point.clone().sub(this.data!.position));
                if (raycastResult.normalInWcs) 
                    this.data!.textNormal.copy(raycastResult.normalInWcs);
                
                this.data!.textUp = getUpDirectionForAnnotationByPlaneNormal(raycastResult.normalInWcs ?? new THREE.Vector3(0,0,1), this.modelRotationOffset);

                this.getObject().geometry.update(this.data!, false);
                break;

        }

        return true;
    }

    point(raycastResult: RaycastIntersection): boolean {
        
        switch (this.cmdState) {
            case AddTextAnnotationCmdState.SETTING_FIRST_PT:
                const firstPoint = raycastResult.point.clone();
                super.addRecoil(firstPoint);
                super.setParent(raycastResult.object);
                this.data = new TextAnnotationData(firstPoint.clone(), new THREE.Vector3);
                this.data.textNormal = this.planeGridHelper.activeNormal?.clone() || (raycastResult.normalInWcs ? raycastResult.normalInWcs.clone() : new THREE.Vector3(0, 0, 1));

                this.showInfoMessage('command.addTextAnnotation.enterText'); // Hide info message while user is entering text
                this.showPlaneGrid(firstPoint.clone(), null);
                this.showSwitchPlaneButton();

                // Usage in an async context:
                (async () => {
                const result = await this.promptTwoFields();
                if (result && this.data) {
                    this.data.text = result.note;
                    this.data.fontSize = result.fontSize;

                    this.cmdState = AddTextAnnotationCmdState.SETTING_OFFSET;
                    this.showInfoMessage('command.addTextAnnotation.setTextPosition');
                    this.enableReferenceLine();
                    this.setReferencePoint(firstPoint.clone());

                } else {
                    this.end();
                }
                })();


                
                const normalToUse = this.planeGridHelper.activeNormal || raycastResult.normalInWcs;
                if (normalToUse) {
                    this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_PLANE);
                    this.canvasInputListener.setTargetPlane(new THREE.Plane().setFromNormalAndCoplanarPoint(   
                        normalToUse.clone(),
                        raycastResult.point.clone()));
                    }
                else {
                    this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
                }
                
                break;

            case AddTextAnnotationCmdState.SETTING_OFFSET:
                // Save to document
                if (!this.persistEntity(this.data!)) {
                    console.error("Failed to save text annotation. Please try again.");
                    alert("Failed to save text annotation. Please try again.");
                    return true;
                }
            
                this.end();
                break;
        }

        return true;
    }

    end() {
        this.deactivate();
        super.end();
        this.removeFromStack();
    }

    onSwitchPlane(): void {
        super.onSwitchPlane();
        if (this.cmdState === AddTextAnnotationCmdState.SETTING_OFFSET && this.data) {
            const normalToUse = this.planeGridHelper.activeNormal;
            if (normalToUse) {
                this.canvasInputListener.setTargetPlane(
                    new THREE.Plane().setFromNormalAndCoplanarPoint(normalToUse.clone(), this.data.position.clone())
                );
            }
        }
    }

    rightClick(event: MouseEvent, raycaster: THREE.Raycaster, targets: THREE.Object3D[]) {
        this.onSwitchPlane();
    }

    // User can change before first point has been set
    // After that, command is terminated is viewer mode changes
    onViewerModeChange(): void {
        switch (this.cmdState) {
            case AddTextAnnotationCmdState.SETTING_FIRST_PT:
                break;  
            default:
                this.end();
                break;
        }
    }

    async promptTwoFields(): Promise<{ note: string; fontSize: number } | null> {
        return useTextAnnotationDialogStore.getState().showDialog();
    }
}